/**
 * One assignment, student view — `/assignments/$assignmentId`.
 *
 * Deliberately a single scrolling page rather than tabs: the brief, the corrections and the submit box
 * are one task, and a student acting on a returned attempt needs to read the comments and re-upload
 * without navigating between them.
 *
 * Returned work is surfaced first, above the brief. Everything a student sees here is what the SERVER
 * decided they may see — staff-only notes are already stripped from the thread, and `canSubmit` is
 * computed by the same policy the submit endpoint enforces.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useMyAssignment } from "@/hooks/use-assignments";
import { BriefMediaList } from "@/components/assignments/brief-media-list";
import { CorrectionThread } from "@/components/assignments/correction-thread";
import { SubmissionFileList } from "@/components/assignments/submission-file-list";
import { SubmissionStatusBadge } from "@/components/assignments/submission-status-badge";
import { SubmitWorkForm } from "@/components/assignments/submit-work-form";
import { SubmissionStatus } from "@/lib/api/assignments";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CheckCircle2,
  Clock,
  Lock,
  RotateCcw,
  Trophy,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/assignments_/$assignmentId")({
  component: StudentAssignmentPage,
});

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function StudentAssignmentPage() {
  const { assignmentId } = Route.useParams();
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useMyAssignment(assignmentId);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-4 md:p-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/assignments">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to assignments
          </Link>
        </Button>
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : "This assignment could not be loaded."}
        </p>
      </div>
    );
  }

  const submission = data.mySubmission;
  const attempts = submission?.attempts ?? [];
  const latest = attempts.length > 0 ? attempts[attempts.length - 1] : null;
  const isGraded = data.myStatus === SubmissionStatus.Graded;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/assignments">
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to assignments
          </Link>
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">{data.title}</h1>
              <SubmissionStatusBadge status={data.myStatus} />
              {data.isOverdue && (
                <Badge
                  variant="outline"
                  className="gap-1 border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-400"
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden />
                  Overdue
                </Badge>
              )}
            </div>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span>
                {data.courseName}
                {data.moduleTitle && ` · ${data.moduleTitle}`}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                {data.dueDate ? formatWhen(data.dueDate) : "No deadline"}
              </span>
              <span>
                {data.allowResubmission
                  ? data.maxAttempts
                    ? `Up to ${data.maxAttempts} attempts`
                    : "Unlimited attempts"
                  : "Single attempt"}
              </span>
            </p>
          </div>

          {isGraded && (
            <Card className="border-emerald-500/40">
              <CardContent className="flex items-center gap-3 py-3">
                <Trophy className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                <div>
                  <p className="text-lg font-semibold">
                    {data.myGrade} / {data.pointsPossible}
                  </p>
                  <p className="text-xs text-muted-foreground">Final grade</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ── Returned for revision: the one thing that needs action ─────────── */}
      {data.needsRevision && latest && (
        <Card className="border-indigo-500/50 bg-indigo-500/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <RotateCcw className="h-4 w-4 text-indigo-600 dark:text-indigo-400" aria-hidden />
              Your reviewer sent this back
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {latest.feedback ? (
              <p className="whitespace-pre-wrap text-sm">{latest.feedback}</p>
            ) : (
              <p className="text-sm text-muted-foreground">
                See the corrections in the conversation below.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Returned {formatWhen(latest.reviewedAt)}
              {latest.reviewedByName && ` by ${latest.reviewedByName}`}. Fix the points raised and
              submit a revision — attempt {latest.attemptNumber} is kept either way.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ── Locked: the brief and the submit box are both withheld ─────────────
          The server sends no description and no attachments while `isLocked`, so rendering the
          usual sections would show "no written instructions" and an empty media list — which reads
          as an empty assignment rather than a shut one. This replaces both. */}
      {data.isLocked ? (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">The brief</h2>
          <Card className="border-amber-500/40 bg-amber-500/5">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden />
                Finish the lessons first
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                This assignment opens once you have completed everything it covers. Until then the
                brief and its {data.attachmentCount > 0 ? `${data.attachmentCount} file(s) ` : ""}
                stay closed.
              </p>
              <ul className="space-y-1.5">
                {data.lockedByModules.map((module) => (
                  <li key={module.moduleId} className="flex items-center gap-2 text-sm">
                    <Clock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                    <span>{module.title}</span>
                  </li>
                ))}
              </ul>
              <Button asChild size="sm" variant="outline">
                <Link to="/training">
                  Go to the lessons
                  <ArrowLeft className="ml-1 h-4 w-4 rotate-180" aria-hidden />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </section>
      ) : (
        <>
          {/* ── The brief ──────────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">The brief</h2>
            <Card>
              <CardContent className="pt-6">
                {data.description ? (
                  <p className="whitespace-pre-wrap text-sm">{data.description}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No written instructions for this assignment.
                  </p>
                )}
              </CardContent>
            </Card>
            <BriefMediaList
              attachments={data.attachments}
              emptyDescription="Your team leader has not attached any media to this brief."
            />
          </section>

          <Separator />

          {/* ── Submit ─────────────────────────────────────────────────────── */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">
              {data.myAttemptCount === 0 ? "Submit your work" : "Submit a revision"}
            </h2>
            <Card>
              <CardContent className="pt-6">
                <SubmitWorkForm
                  assignmentId={data.id}
                  canSubmit={data.canSubmit}
                  cannotSubmitReason={data.cannotSubmitReason}
                  attemptNumber={data.myAttemptCount + 1}
                  isRevision={data.myAttemptCount > 0}
                />
              </CardContent>
            </Card>
          </section>
        </>
      )}

      {/* ── History ────────────────────────────────────────────────────────── */}
      {attempts.length > 0 && (
        <>
          <Separator />
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">
              Your attempts{" "}
              <span className="text-sm font-normal text-muted-foreground">({attempts.length})</span>
            </h2>
            {/* Newest first here, unlike the reviewer's view: a student's own most recent submission is
                the one they care about, and older attempts are reference. */}
            {[...attempts].reverse().map((attempt) => (
              <Card
                key={attempt.id}
                className={cn(attempt.id === latest?.id && "border-primary/40")}
              >
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="flex items-center gap-2 text-sm">
                      Attempt {attempt.attemptNumber}
                      {attempt.id === latest?.id && (
                        <Badge variant="secondary" className="text-[10px]">
                          Latest
                        </Badge>
                      )}
                      {attempt.isLate && (
                        <Badge
                          variant="outline"
                          className="border-orange-500/40 bg-orange-500/10 text-[10px] text-orange-700 dark:text-orange-400"
                        >
                          Late
                        </Badge>
                      )}
                    </CardTitle>
                    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" aria-hidden />
                      {formatWhen(attempt.submittedAt)}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {attempt.textContent && (
                    <p className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-sm">
                      {attempt.textContent}
                    </p>
                  )}
                  <SubmissionFileList files={attempt.files} />

                  {attempt.reviewedAt && (
                    <div className="rounded-md border bg-muted/20 p-3 text-sm">
                      <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        {attempt.status === SubmissionStatus.Graded ? (
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5 text-indigo-600" aria-hidden />
                        )}
                        {attempt.status === SubmissionStatus.Graded
                          ? `Graded ${attempt.grade} / ${data.pointsPossible}`
                          : "Returned for revision"}
                        {` · ${formatWhen(attempt.reviewedAt)}`}
                      </p>
                      {attempt.feedback && (
                        <p className="whitespace-pre-wrap">{attempt.feedback}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </section>
        </>
      )}

      {/* ── Conversation ───────────────────────────────────────────────────── */}
      {submission && (
        <>
          <Separator />
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Conversation with your reviewer</h2>
            <CorrectionThread
              submissionId={submission.id}
              comments={submission.comments}
              currentUserId={user?.id}
              isStaff={false}
              attemptId={latest?.id ?? null}
            />
          </section>
        </>
      )}
    </div>
  );
}
